import type { FormEvent, ReactElement } from 'react';
import { useState } from 'react';
import { Plus } from 'lucide-react';

import {
    useCreateAdminPiiConsentTextMutation,
    useListAdminPiiConsentTextsQuery
} from '@/api/piiConsentTextsApi';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';

const VERSION_REGEX = /^[a-zA-Z0-9.\-_]{1,20}$/;
const MIN_TEXT_LEN = 10;

interface FormState {
    version: string;
    textRu: string;
}

interface FormErrors {
    version?: string;
    textRu?: string;
}

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU');
}

export function PiiConsentTextsPage(): ReactElement {
    const { data, isLoading, isError } = useListAdminPiiConsentTextsQuery();
    const [createText, { isLoading: isCreating }] = useCreateAdminPiiConsentTextMutation();

    const [isModalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState<FormState>({ version: '', textRu: '' });
    const [errors, setErrors] = useState<FormErrors>({});
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [pageError, setPageError] = useState<string | null>(null);
    const [viewing, setViewing] = useState<{ version: string; textRu: string } | null>(null);

    const items = data?.items ?? [];

    const openCreate = (): void => {
        setForm({ version: '', textRu: '' });
        setErrors({});
        setSubmitError(null);
        setModalOpen(true);
    };

    const closeCreate = (): void => {
        setModalOpen(false);
        setForm({ version: '', textRu: '' });
        setErrors({});
        setSubmitError(null);
    };

    const validate = (): boolean => {
        const next: FormErrors = {};
        if (form.version.trim() === '') {
            next.version = 'Версия обязательна';
        } else if (!VERSION_REGEX.test(form.version)) {
            next.version = 'Только латиница, цифры, точка, дефис, подчёркивание (до 20)';
        }
        if (form.textRu.trim().length < MIN_TEXT_LEN) {
            next.textRu = `Минимум ${MIN_TEXT_LEN} символов`;
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setSubmitError(null);
        if (!validate()) {
            return;
        }
        try {
            await createText({
                version: form.version.trim(),
                textRu: form.textRu
            }).unwrap();
            closeCreate();
        } catch {
            setSubmitError('Не удалось опубликовать версию');
        }
    };

    return (
        <div data-testid="pii-consent-texts-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Тексты согласия на ПД</h1>
                <button
                    type="button"
                    onClick={openCreate}
                    data-testid="pii-consent-create-button"
                    className="inline-flex items-center gap-2 rounded bg-gray-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    <Plus size={14} />
                    Создать новую версию
                </button>
            </div>

            <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                Публикация новой версии автоматически деактивирует предыдущую активную.
                Принятые ранее пользователями версии остаются в истории и не сбрасываются.
                Согласуйте текст с юристом до публикации.
            </div>

            {pageError !== null && pageError !== '' && (
                <div
                    className="flex items-center justify-between rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="pii-consent-page-error"
                >
                    <span>{pageError}</span>
                    <button
                        type="button"
                        onClick={() => setPageError(null)}
                        className="text-xs text-red-700 underline"
                    >
                        Закрыть
                    </button>
                </div>
            )}

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="pii-consent-error">
                    Не удалось загрузить версии
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="pii-consent-table"
                >
                    <div className="grid grid-cols-[140px_180px_120px_minmax(0,1fr)_120px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>Версия</span>
                        <span>Опубликована</span>
                        <span>Статус</span>
                        <span>Превью</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Версии не созданы
                        </div>
                    )}

                    {items.map((item) => (
                        <div
                            key={item.version}
                            className={`grid grid-cols-[140px_180px_120px_minmax(0,1fr)_120px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0 ${
                                item.isActive ? 'bg-green-50' : ''
                            }`}
                            data-testid={`pii-consent-row-${item.version}`}
                        >
                            <span className="truncate font-mono text-gray-900">
                                {item.version}
                            </span>
                            <span className="text-gray-700">
                                {formatDateTime(item.publishedAt)}
                            </span>
                            <span>
                                {item.isActive ? (
                                    <span
                                        className="inline-block rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
                                        data-testid={`pii-consent-active-${item.version}`}
                                    >
                                        Активна
                                    </span>
                                ) : (
                                    <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                                        Архив
                                    </span>
                                )}
                            </span>
                            <span className="truncate text-xs text-gray-600">
                                {item.textRu.slice(0, 120)}
                                {item.textRu.length > 120 ? '...' : ''}
                            </span>
                            <span className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() =>
                                        setViewing({
                                            version: item.version,
                                            textRu: item.textRu
                                        })
                                    }
                                    data-testid={`pii-consent-view-${item.version}`}
                                    className="rounded border border-gray-300 px-3 py-1 text-xs text-gray-700 transition hover:bg-gray-100"
                                >
                                    Посмотреть
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={closeCreate}
                title="Новая версия текста согласия"
                testId="pii-consent-create-modal"
            >
                <form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                    data-testid="pii-consent-create-form"
                >
                    <Input
                        label="Версия"
                        value={form.version}
                        onChange={(e) => setForm({ ...form, version: e.target.value })}
                        placeholder="v1.0"
                        error={errors.version}
                        data-testid="pii-consent-form-version"
                        autoFocus
                    />
                    <Textarea
                        label="Текст согласия (на русском)"
                        value={form.textRu}
                        onChange={(e) => setForm({ ...form, textRu: e.target.value })}
                        rows={12}
                        error={errors.textRu}
                        data-testid="pii-consent-form-text"
                    />

                    {submitError !== null && submitError !== '' && (
                        <div
                            className="text-sm text-red-600"
                            data-testid="pii-consent-form-error"
                        >
                            {submitError}
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={closeCreate}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating}
                            data-testid="pii-consent-form-submit"
                            className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
                        >
                            Опубликовать
                        </button>
                    </div>
                </form>
            </Modal>

            <Modal
                isOpen={viewing !== null}
                onClose={() => setViewing(null)}
                title={`Версия ${viewing?.version ?? ''}`}
                testId="pii-consent-view-modal"
            >
                <pre
                    className="max-h-[60vh] overflow-auto whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-800"
                    data-testid="pii-consent-view-text"
                >
                    {viewing?.textRu ?? ''}
                </pre>
                <div className="mt-4 flex justify-end">
                    <button
                        type="button"
                        onClick={() => setViewing(null)}
                        className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                    >
                        Закрыть
                    </button>
                </div>
            </Modal>
        </div>
    );
}
